/**
 * Dashboard Service
 *
 * Handles API calls for donor dashboard data
 */

import { getApiClient } from './client';
import type {
  DashboardOverviewResponse,
  DashboardChartsResponse,
  DashboardCommunityResponse,
  DashboardChartsOptions,
} from '@/types/dashboard.types';

/**
 * Fetch dashboard overview data
 * Includes summary stats, recent donations, recurring donations, and tax receipts
 *
 * @param companyId - The company ID
 * @param limit - Number of recent donations to return (default: 10)
 * @returns Promise with dashboard overview data
 * @throws Error if request fails
 */
export async function getDashboardOverview(
  companyId: string | number,
  limit: number = 10
): Promise<DashboardOverviewResponse> {
  try {
    const apiClient = getApiClient();
    const response = await apiClient.get<DashboardOverviewResponse>(
      `/dashboard/overview/${companyId}`,
      {
        params: { limit },
      }
    );

    return response.data;
  } catch (error) {
    if (error instanceof Error) {
      throw new Error(`Failed to fetch dashboard overview: ${error.message}`);
    }
    throw error;
  }
}

/**
 * Fetch dashboard charts data
 * Includes donation trends and category breakdown
 *
 * @param companyId - The company ID
 * @param options - Chart query options (period, months)
 * @returns Promise with dashboard charts data
 * @throws Error if request fails
 */
export async function getDashboardCharts(
  companyId: string | number,
  options?: DashboardChartsOptions
): Promise<DashboardChartsResponse> {
  try {
    const apiClient = getApiClient();
    const { period = 'monthly', months = 12 } = options || {};

    const response = await apiClient.get<DashboardChartsResponse>(
      `/dashboard/charts/${companyId}`,
      {
        params: { period, months },
      }
    );

    return response.data;
  } catch (error) {
    if (error instanceof Error) {
      throw new Error(`Failed to fetch dashboard charts: ${error.message}`);
    }
    throw error;
  }
}

/**
 * Fetch dashboard community data
 * Includes impact metrics, upcoming events, and announcements
 *
 * @param companyId - The company ID
 * @param eventLimit - Number of upcoming events to return (default: 5)
 * @param announcementLimit - Number of announcements to return (default: 5)
 * @returns Promise with dashboard community data
 * @throws Error if request fails
 */
export async function getDashboardCommunity(
  companyId: string | number,
  eventLimit: number = 5,
  announcementLimit: number = 5
): Promise<DashboardCommunityResponse> {
  try {
    const apiClient = getApiClient();
    const response = await apiClient.get<DashboardCommunityResponse>(
      `/dashboard/community/${companyId}`,
      {
        params: {
          event_limit: eventLimit,
          announcement_limit: announcementLimit,
        },
      }
    );

    return response.data;
  } catch (error) {
    if (error instanceof Error) {
      throw new Error(`Failed to fetch dashboard community: ${error.message}`);
    }
    throw error;
  }
}
